import { useMsal } from '@azure/msal-react';
import { useSyncExternalStore } from 'react';

import {
  getImpersonationEmail,
  subscribeImpersonation,
} from './impersonation';

export interface AuthUser {
  email: string;
  name: string;
}

/**
 * Returns the currently authenticated user's email and display name.
 *
 * IMPORTANT: When impersonation is active, this still returns the *real*
 * authenticated user — used by admin UI gates that should never disappear
 * mid-session (e.g. the impersonation exit banner). For the *effective*
 * identity that backend filters use, see `useEffectiveUser`.
 */
export function useAuthUser(): AuthUser {
  const { accounts } = useMsal();
  const account = accounts[0];
  if (!account) {
    return { email: '', name: '' };
  }
  return {
    email: account.username || '',
    name: account.name || account.username || '',
  };
}

export interface EffectiveUser {
  realEmail: string;
  realName: string;
  effectiveEmail: string;
  // Display name for the impersonated identity is best-effort here (the
  // picker provides a friendlier name). When unknown, fall back to the
  // email so the banner is never blank.
  effectiveName: string;
  isImpersonating: boolean;
}

/**
 * Returns both the real and effective identity in one call. Use this when
 * the UI needs to show "Brad → Alice" simultaneously (e.g. the banner).
 *
 * Components that just want "who do queries belong to" should read the
 * effective email — it's what the backend will filter by.
 */
export function useEffectiveUser(): EffectiveUser {
  const real = useAuthUser();
  const target = useSyncExternalStore(
    subscribeImpersonation,
    getImpersonationEmail,
    () => null,
  );

  if (!target || target.toLowerCase() === real.email.toLowerCase()) {
    return {
      realEmail: real.email,
      realName: real.name,
      effectiveEmail: real.email,
      effectiveName: real.name,
      isImpersonating: false,
    };
  }
  return {
    realEmail: real.email,
    realName: real.name,
    effectiveEmail: target,
    effectiveName: target,
    isImpersonating: true,
  };
}
